import { isNil } from 'lodash-es'
import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { Locale, NumberFormatOptions, BooleanFormatOptions } from 'locale'
import { Decimal, DateLike } from 'utils'
import { TypedGetQuickFilterTextParams } from 'view/components/datagrid'

// Formatters

export function makeNumberFormatter(options?: NumberFormatOptions, parenOnNegative?: boolean) {
  return function numberFormatter(params: ValueFormatterParams) {
    return Locale.formatNumber(params.value, {
      ...options,
      ...(params.node?.group && parenOnNegative && { parenOnNegative: true })
    })
  }
}

export const makeStringOrNumberFormatter = (options?: NumberFormatOptions, parenOnNegative?: boolean) => (params: ValueFormatterParams) => {
  if (typeof params.value === 'string') {
    return params.value
  }
  return Locale.formatNumber(params.value, {
    ...options,
    ...(params.node?.group && parenOnNegative && { parenOnNegative: true })
  })
}

export function makeNumberFilterGetter(options?: NumberFormatOptions) {
  return function numberFilterGetter(params: ValueGetterParams) {
    if (typeof params.colDef.valueGetter !== 'function') {
      throw new Error('valueGetter must be a function to create a filterValueGetter')
    }
    let value = params.colDef.valueGetter(params)
    if (isNil(value)) {
      return value
    }
    if (options?.decimalShift) {
      value = Decimal.shift(value, options.decimalShift)
    }
    if (options?.flipSign) {
      value = Decimal.multiply(value, -1)
    } else if (options?.absoluteIncrease) {
      value = Decimal.add(100, value)
    } else if (options?.absoluteDecrease) {
      value = Decimal.subtract(100, value)
    }
    return value
  }
}

export function makeBooleanFormatter(options?: BooleanFormatOptions) {
  return function booleanFormatter(params: ValueFormatterParams) {
    /* Don't display the default value in group cells */
    if (params.node && params.node.group) {
      // Group Cell
      return Locale.formatBoolean(params.value)
    } else if (params.node) {
      // Cell
      return Locale.formatBoolean(params.value, options)
    } else {
      // Filter
      return Locale.formatBoolean(params.value === 'true')
    }
  }
}

export function makeDateFormatter() {
  return function dateFormatter(params: ValueFormatterParams) {
    const { context, value } = params
    return Locale.formatDate(value, context.userPreference.dateFormat)
  }
}

export function makeTimeFormatter() {
  return function timeFormatter(params: ValueFormatterParams) {
    return Locale.formatTime(params.value)
  }
}

export function makeDateAndTimeFormatter() {
  return function dateAndTimeFormatter(params: ValueFormatterParams) {
    const { context, value } = params
    return Locale.formatDateAndTime(value, context.userPreference.dateFormat)
  }
}

export function makeTimestampFormatter() {
  return function timestampFormatter(params: ValueFormatterParams) {
    const { context, value } = params
    return Locale.formatTimestamp(value, context.userPreference.dateFormat)
  }
}

// Quick Filter Text Getter

export function makeNumberQuickFilterTextGetter(options?: NumberFormatOptions) {
  return function numberQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<number>) {
    return Locale.formatNumber(params.value, { separator: false, ...options })
  }
}

export function makeBooleanQuickFilterTextGetter(options?: BooleanFormatOptions) {
  return function booleanQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<boolean>) {
    return Locale.formatBoolean(params.value, options)
  }
}

export function dateQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<DateLike>) {
  return Locale.formatDate(params.value, params.context.userPreference.dateFormat)
}

export function timeQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<DateLike>) {
  return Locale.formatTime(params.value)
}
